import { useEffect, useRef, useState } from "react";
import { Dialog } from "primereact/dialog";
import { Calendar } from "primereact/calendar";
import { Button } from "primereact/button";
import { SelectButton } from "primereact/selectbutton";

type AttachSignedDocumentDialogProps = {
  visible: boolean;
  submitting: boolean;
  removing: boolean;
  documents: { id: string; fileName: string; kind?: "signed" | "attachment" }[];
  onSubmit: (files: File[], signedDate: Date | null, kind: "signed" | "attachment") => void;
  onRemove: (documentId: string) => void;
  onCancel: () => void;
};

export function AttachSignedDocumentDialog({
  visible,
  submitting,
  removing,
  documents,
  onSubmit,
  onRemove,
  onCancel,
}: AttachSignedDocumentDialogProps) {
  const [files, setFiles] = useState<File[]>([]);
  const [signedDate, setSignedDate] = useState<Date | null>(null);
  const [kind, setKind] = useState<"signed" | "attachment">("signed");
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (visible) {
      setFiles([]);
      setSignedDate(null);
      setKind("signed");
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }, [visible]);

  const footer = (
    <div>
      <Button label="Cancel" className="p-button-text" onClick={onCancel} disabled={submitting} />
      <Button
        label="Attach"
        loading={submitting}
        disabled={!files.length || (kind === "signed" && !signedDate)}
        onClick={() => files.length && onSubmit(files, signedDate, kind)}
      />
    </div>
  );

  const signedDocuments = documents.filter((doc) => (doc.kind ?? "signed") === "signed");
  const attachmentDocuments = documents.filter((doc) => (doc.kind ?? "signed") === "attachment");

  const renderDocumentList = (docs: { id: string; fileName: string }[]) => (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
      {docs.map((doc) => (
        <div
          key={doc.id}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "0.5rem",
          }}
        >
          <span>{doc.fileName}</span>
          <Button
            icon="pi pi-trash"
            className="p-button-text p-button-danger"
            disabled={removing || submitting}
            onClick={() => onRemove(doc.id)}
          />
        </div>
      ))}
    </div>
  );

  return (
    <Dialog
      visible={visible}
      header="Signed documents"
      draggable={false}
      onHide={submitting ? () => undefined : onCancel}
      closable={!submitting}
      closeOnEscape={!submitting}
      footer={footer}
      style={{ width: "30rem" }}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
        {signedDocuments.length > 0 && (
          <div>
            <label style={{ display: "block", marginBottom: "0.35rem" }}>Signed contract</label>
            {renderDocumentList(signedDocuments)}
          </div>
        )}
        {attachmentDocuments.length > 0 && (
          <div>
            <label style={{ display: "block", marginBottom: "0.35rem" }}>Attachments</label>
            {renderDocumentList(attachmentDocuments)}
          </div>
        )}
        <div>
          <label style={{ display: "block", marginBottom: "0.35rem" }}>Document type</label>
          <SelectButton
            value={kind}
            onChange={(e) => e.value && setKind(e.value)}
            options={[
              { label: "Signed contract", value: "signed" },
              { label: "Attachment", value: "attachment" },
            ]}
          />
        </div>
        <div>
          <label htmlFor="signedDocumentFile" style={{ display: "block", marginBottom: "0.35rem" }}>
            {kind === "signed"
              ? "Signed document (pdf, jpg, jpeg, png)"
              : "Attachment (pdf, jpg, jpeg, png, xls, xlsx, doc, docx)"}
          </label>
          <input
            id="signedDocumentFile"
            ref={fileInputRef}
            type="file"
            multiple
            accept={
              kind === "signed"
                ? ".pdf,.jpg,.jpeg,.png"
                : ".pdf,.jpg,.jpeg,.png,.xls,.xlsx,.doc,.docx"
            }
            onChange={(e) => setFiles(Array.from(e.target.files ?? []))}
          />
        </div>
        {kind === "signed" ? (
          <div>
            <label htmlFor="signedDocumentDate" style={{ display: "block", marginBottom: "0.35rem" }}>
              Date signed
            </label>
            <Calendar
              inputId="signedDocumentDate"
              value={signedDate ?? undefined}
              onChange={(e) => setSignedDate((e.value as Date) ?? null)}
              dateFormat="mm/dd/yy"
              placeholder="MM/DD/YY"
              maxDate={new Date()}
              showIcon
            />
          </div>
        ) : null}
      </div>
    </Dialog>
  );
}
